/**
 * i18n Translations
 *
 * Centralized UI text translations for all supported languages
 */

import type { LanguageCode } from '@/types/user';

export type TranslationKey = keyof typeof translations.en;

export const translations = {
  // English (Default)
  en: {
    // Navigation
    'nav.friends': 'Friends',
    'nav.messages': 'Messages',
    'nav.profile': 'Profile',
    'nav.settings': 'Settings',

    // Common
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.close': 'Close',
    'common.confirm': 'Confirm',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.retry': 'Try again',
    'common.search': 'Search',
    'common.back': 'Go back',
    'common.done': 'Done',
    'common.change': 'Change',
    'common.saved': 'Saved',
    'common.saving': 'Saving...',
    'common.copied': 'Copied!',
    'common.dismiss': 'Dismiss',
    'common.sending': 'Sending...',
    'common.creating': 'Creating...',
    'common.opening': 'Opening...',

    // Profile Page
    'profile.title': 'Profile',
    'profile.tag': 'Tag',
    'profile.displayName': 'Nickname',
    'profile.displayNamePlaceholder': 'e.g. John, Cook',
    'profile.username': 'Username',
    'profile.usernamePlaceholder': 'lowercase letters and numbers only',
    'profile.status': 'Status',
    'profile.statusPlaceholder': "What's on your mind?",
    'profile.primaryLanguage': 'Language',
    'profile.timezone': 'Timezone',
    'profile.selectTimezone': 'Select timezone',
    'profile.logout': 'Log out',
    'profile.loggingOut': 'Logging out...',
    'profile.logoutConfirmTitle': 'Log out',
    'profile.logoutConfirmMessage': 'Are you sure you want to log out?',
    'profile.changeUsernameTitle': 'Change username?',
    'profile.changeUsernameMessage': 'Changing your username will generate a new tag.',
    'profile.saveChanges': 'Save',

    // Settings Page
    'settings.title': 'Settings',
    'settings.notifications': 'Notifications',
    'settings.pushNotifications': 'Push Notifications',
    'settings.enableNotifications': 'Enable notifications',
    'settings.disableNotifications': 'Disable notifications',
    'settings.notificationsBlocked': 'Notifications are blocked in your browser settings.',
    'settings.notificationsBlockedHelp': "Click the lock icon in your browser's address bar → Site settings → Allow notifications",
    'settings.privacy': 'Privacy',
    'settings.blockedUsers': 'Blocked Users',
    'settings.app': 'App',
    'settings.installApp': 'Install App',
    'settings.installAppDesc': 'Install Talksy as an app for quick access',
    'settings.installAppManual': 'Click the install icon in your browser address bar, or use browser menu → "Install app"',
    'settings.installing': 'Installing...',
    'settings.installed': 'App Installed',
    'settings.installedDesc': 'Talksy is installed on your device',
    'settings.installGuideTitle': 'Install Talksy',
    'settings.installStep1': 'Click the install icon in address bar',
    'settings.installStep2': 'Or select from browser menu',
    'settings.installStep3': 'Confirm to install',
    'settings.installMobileTitle': 'Add to Home Screen',
    'settings.installMobileDesc': 'Install Talksy for the best experience',
    'settings.installIosStep1': '···',
    'settings.installIosSafariHint': 'Bottom right ···',
    'settings.installIosStep2': 'Share',
    'settings.installIosStep3': 'More',
    'settings.installIosStep4': 'Add to Home',
    'settings.installIosStep5': 'Add',
    'settings.installIosNote': 'Use Safari to install',
    'settings.installAndroidStep1': 'Tap the menu (⋮) button',
    'settings.installAndroidStep2': 'Tap "Add to Home screen"',
    'settings.installAndroidStep3': 'Tap "Add" to confirm',
    'settings.installNow': 'Install Now',
    'settings.useNativeBrowser': 'Open in {browser} to install',

    // Friends Page
    'friends.title': 'Friends',
    'friends.addFriend': 'Add Friend',
    'friends.pendingRequests': 'Pending Requests',
    'friends.allFriends': 'All Friends',
    'friends.noFriends': 'No friends yet',
    'friends.noFriendsDesc': 'Add friends and start chatting',
    'friends.addFriendButton': 'Add Friend',
    'friends.welcome': 'Welcome, {name}!',
    'friends.createChannel': 'Create Channel',
    'friends.copyTag': 'Copy Tag',
    'friends.sentRequest': 'Sent you a friend request',
    'friends.accept': 'Accept',
    'friends.decline': 'Decline',
    'friends.message': 'Message',
    'friends.requestSent': 'Request Sent',
    'friends.requestReceived': 'Sent You a Request',
    'friends.friendsSince': 'Friends since',
    'friends.wantsToBeYourFriend': 'Wants to be your friend',
    'friends.acceptRequest': 'Accept Request',
    'friends.requestedOn': 'Requested on',
    'friends.loadingFriends': 'Loading friends...',

    // Add Friend Modal
    'addFriend.title': 'Add Friend',
    'addFriend.subtitle': 'Grow your circle on Talksy',
    'addFriend.placeholder': 'Enter Username#0000',
    'addFriend.sendRequest': 'Send Friend Request',
    'addFriend.errorEmpty': 'Please enter a tag',
    'addFriend.errorFormat': 'Invalid format. Please use Username#0000 format',
    'addFriend.errorSelf': 'You cannot send a friend request to yourself',
    'addFriend.errorNotFound': 'User with this tag not found',
    'addFriend.errorAlreadyFriend': 'Already friends',
    'addFriend.errorRequestSent': 'Friend request already sent',
    'addFriend.errorRequestReceived': 'This user has already sent you a friend request. Check your friend requests tab',
    'addFriend.errorSendFailed': 'Failed to send friend request',
    'addFriend.errorGeneral': 'An error occurred while sending the friend request',
    'addFriend.successMessage': 'Friend request sent to {name}',

    // Create Channel Modal
    'createChannel.title': 'Create Channel',
    'createChannel.subtitle': 'Create a space for your group',
    'createChannel.channelName': 'CHANNEL NAME',
    'createChannel.placeholder': 'e.g. Gaming Squad',
    'createChannel.upload': 'Upload',
    'createChannel.createButton': 'Create Channel',
    'createChannel.errorImageOnly': 'Only image files can be uploaded',
    'createChannel.errorFileSize': 'File size must be 5MB or less',
    'createChannel.errorUploadFailed': 'Failed to upload icon',
    'createChannel.errorCreateFailed': 'Failed to create channel',

    // Blocked Users
    'blockedUsers.title': 'Blocked Users',
    'blockedUsers.noBlockedUsers': 'No blocked users',
    'blockedUsers.noBlockedUsersDesc': 'Users you block will appear here',
    'blockedUsers.unblock': 'Unblock',
    'blockedUsers.unblockTitle': 'Unblock User',
    'blockedUsers.unblockConfirm': 'Are you sure you want to unblock',
    'blockedUsers.unblockInfo': 'They will be able to send you friend requests and messages again.',

    // Sidebar
    'sidebar.newConversation': 'New conversation',
    'sidebar.addFriend': 'Add Friend',
    'sidebar.addFriendDesc': 'New direct message',
    'sidebar.newChannel': 'New Channel',
    'sidebar.newChannelDesc': 'Create a community',

    // Network Banner
    'network.offline': 'Internet connection lost',
    'network.reconnecting': 'Reconnecting...',
    'network.reconnect': 'Reconnect',

    // Login Page
    'login.termsNotice': 'By logging in, you agree to our Terms of Service and Privacy Policy.',

    // Empty States
    'empty.conversations': 'No conversations yet',
    'empty.conversationsDesc': 'Start a conversation with friends',

    // Confirm Dialog
    'confirm.deleteTitle': 'Delete',
    'confirm.blockTitle': 'Block User',

    // Friend Profile
    'friendProfile.inviteToChannel': 'Invite to Channel',
    'friendProfile.deleteFriend': 'Delete Friend',
    'friendProfile.blockUser': 'Block User',
    'friendProfile.unblockUser': 'Unblock User',
    'friendProfile.moreOptions': 'More options',

    // Channel Members
    'channel.members.title': 'Channel Members',
    'channel.members.owner': 'Owner',
    'channel.members.admin': 'Admin',
    'channel.members.member': 'Member',
    'channel.members.me': '(me)',
    'channel.members.kick': 'Kick',
    'channel.members.kickConfirm': 'Remove {name} from the channel?',
    'channel.members.transferOwnership': 'Transfer Ownership',
    'channel.members.transferConfirm': 'Transfer ownership to {name}?\n\nYou will become an admin after transfer.',
    'channel.members.ownerCount': '{count} owner',
    'channel.members.adminCount': '{count} admin',
    'channel.members.memberCount': '{count} member',
    'channel.members.noMembers': 'No members',
    'channel.members.totalCount': '{count} members',

    // Channel Settings
    'channel.settings.title': 'Channel Settings',
    'channel.settings.name': 'Channel Name',
    'channel.settings.namePlaceholder': 'Enter channel name',
    'channel.settings.description': 'Description',
    'channel.settings.descriptionPlaceholder': 'A short description of the channel',
    'channel.settings.icon': 'Channel Icon',
    'channel.settings.iconChange': 'Click to change',
    'channel.settings.save': 'Save',
    'channel.settings.leave': 'Leave Channel',
    'channel.settings.leaveWarning': 'Once you leave, you cannot rejoin unless invited again.',
    'channel.settings.leaveOwnerWarning': 'As owner, you can only leave after transferring ownership.',
    'channel.settings.errorNameEmpty': 'Please enter channel name',
    'channel.settings.errorNameShort': 'Channel name must be at least 2 characters',
    'channel.settings.errorNameLong': 'Channel name can be max 50 characters',
    'channel.settings.errorFileSize': 'File size must be 5MB or less',
    'channel.settings.errorUpload': 'Failed to upload icon',
    'channel.settings.errorSave': 'Failed to save channel settings',
    'channel.settings.errorLeave': 'Failed to leave channel',
    'channel.settings.errorOwnerLeave': 'Owner must transfer ownership or delete the channel',
    'channel.settings.errorNeedAdmin': 'Channel requires at least 1 admin',

    // Channel Chat
    'channel.chat.messagePlaceholder': 'Message #{name}',

    // Channel Invite
    'channel.invite.title': 'Invite to Channel',
    'channel.invite.expiration': 'Expires in',
    'channel.invite.1h': '1 hour',
    'channel.invite.1d': '1 day',
    'channel.invite.7d': '7 days',
    'channel.invite.never': 'Never',
    'channel.invite.generate': 'Generate Invite Link',
    'channel.invite.copied': 'Copied to clipboard!',
    'channel.invite.generated': 'Link generated',
    'channel.invite.copyAgain': 'Copy again',
    'channel.invite.noExpiry': 'No expiry',
    'channel.invite.errorGenerate': 'Failed to generate invite link',
    'channel.invite.loginRequired': 'Login required',
    'channel.invite.loginRequiredDesc': 'Please log in to join a channel.',
    'channel.invite.login': 'Log in',
    'channel.invite.noCode': 'No invite code',
    'channel.invite.noCodeDesc': 'Please access via a valid invite link.',
    'channel.invite.goToChannels': 'Go to channels',

    // Channel Errors
    'channel.error.notFound': 'Channel not found',
    'channel.error.noAccess': 'You do not have access to this channel',
    'channel.error.loadFailed': 'Failed to load channel',
    'channel.error.loginRequired': 'Login required',
    'channel.error.roleChangeFailed': 'Failed to change role',
    'channel.error.cannotManageOwner': 'Cannot change owner\'s role',
    'channel.error.cannotManageAdmin': 'Admin cannot manage other admins',
    'channel.error.transferFailed': 'Failed to transfer ownership',
    'channel.error.kickFailed': 'Failed to kick member',
    'channel.error.kicked': 'You have been kicked from this channel',
    'channel.error.needJoin': 'You must join the channel to send messages',
    'channel.error.ownerCannotLeave': 'Owner must transfer ownership or delete the channel',

    // Channel Join
    'channel.join.join': 'Join',
    'channel.join.cancel': 'Cancel',
    'channel.join.goBack': 'Go back',
    'channel.join.memberCount': '{count} members',
    'channel.join.invalidLink': 'Invalid invite link',
    'channel.join.invalidDesc': 'This invite link does not exist or has been deleted.',
    'channel.join.usedLink': 'Invite link already used',
    'channel.join.usedDesc': 'This invite link has already been used.',
    'channel.join.expiredLink': 'Expired invite link',
    'channel.join.expiredDesc': 'This invite link has expired.',
    'channel.join.selfLink': 'Your own invite link',
    'channel.join.selfDesc': 'This is your own invite link. Redirecting to chat room.',
    'channel.join.maxUsed': 'Max uses reached',
    'channel.join.maxUsedDesc': 'This invite link has reached its maximum uses. Please request a new link.',
    'channel.join.alreadyMember': 'Already a member',
    'channel.join.alreadyMemberDesc': 'You are already a member of this channel.',
    'channel.join.channelFull': 'Channel full',
    'channel.join.channelFullDesc': 'This channel has reached maximum capacity.',
    'channel.join.notFoundLink': 'Invite link not found',
    'channel.join.notFoundDesc': 'Invalid invite link. Please check the link again.',

    // DM / Messages
    'dm.error.notFound': 'Conversation not found',
    'dm.error.loadFailed': 'Failed to load conversation',
    'dm.blocked.byMe': 'You have blocked this user. Unblock to send messages.',
    'dm.blocked.byThem': 'Cannot send messages.',
    'dm.blocked.mutual': 'Blocked user. Cannot send or receive messages.',
    'dm.placeholder': 'Message @{name}',
    'dm.placeholderDefault': 'Enter message...',
    'dm.placeholderBlocked': 'Cannot send messages',
    'dm.offline': 'You are offline. Messages will be sent when connection is restored.',
    'dm.user': 'User',

    // Time
    'time.justNow': 'Just now',
    'time.minuteAgo': '1 min ago',
    'time.minutesAgo': '{count} min ago',
    'time.hourAgo': '1 hr ago',
    'time.hoursAgo': '{count} hr ago',
    'time.yesterday': 'Yesterday',
    'time.dayAgo': '1 day ago',
    'time.daysAgo': '{count} days ago',
    'time.requested': 'Requested ',
    'time.sent': 'Sent ',

    // Language names (for profile display)
    'language.ko': 'Korean',
    'language.en': 'English',
    'language.ja': 'Japanese',
    'language.zh': 'Chinese',
    'language.es': 'Spanish',
    'language.fr': 'French',
    'language.de': 'German',
    'language.pt': 'Portuguese',
    'language.ru': 'Russian',
    'language.ar': 'Arabic',
    'language.hi': 'Hindi',
    'language.vi': 'Vietnamese',
    'language.th': 'Thai',
    'language.id': 'Indonesian',

    // System messages
    'system.memberJoined': '{name} joined the channel',
    'system.memberLeft': '{name} left the channel',
    'system.memberKicked': '{name} was kicked',
    'system.channelUpdated': 'Channel info was updated',
    'system.roleChanged': '{name}\'s role was changed to {role}',
    'system.ownershipTransferred': 'Channel ownership was transferred to {name}',

    // Friend Requests
    'friendRequest.accept': 'Accept',
    'friendRequest.decline': 'Decline',
    'friendRequest.cancel': 'Cancel Request',
    'friendRequest.processing': 'Processing...',
    'friendRequest.acceptFailed': 'Failed to accept',
    'friendRequest.declineFailed': 'Failed to decline',
    'friendRequest.cancelFailed': 'Failed to cancel',
    'friendRequest.noReceived': 'No received requests',
    'friendRequest.noSent': 'No sent requests',
    'friendRequest.noReceivedDesc': 'Friend requests from others will appear here',
    'friendRequest.noSentDesc': 'Your sent friend requests will appear here',

    // Search
    'search.placeholder': 'Search by tag or username',
    'search.clear': 'Clear search',
    'search.noResults': 'No user found for "{query}"',
    'search.tagHelp': 'Enter full tag (e.g. john#A1B2) for exact match',

    // Misc
    'misc.startConversation': 'Start a conversation',
    'misc.waitingForPartner': 'Waiting for partner...',
    'misc.chat': 'Chat',
    'misc.newConversation': 'New Conversation',
    'misc.waiting': 'Waiting...',
    'misc.copyLink': 'Copy link',
    'misc.linkCopied': 'Link copied!',
    'misc.unknownError': 'An unknown error occurred',
    'misc.withdrawnUser': 'Withdrawn user',
    'misc.selectConversation': 'Select a conversation',
    'misc.selectConversationDesc': 'Select a conversation from the list or start a new one.',
    'misc.selectChannel': 'Select a channel',
    'misc.selectChannelDesc': 'Select a channel from the list to view conversations.',
  },

  // Korean
  ko: {
    // Navigation
    'nav.friends': '친구',
    'nav.messages': '메시지',
    'nav.profile': '프로필',
    'nav.settings': '설정',

    // Common
    'common.save': '저장',
    'common.cancel': '취소',
    'common.close': '닫기',
    'common.confirm': '확인',
    'common.delete': '삭제',
    'common.edit': '편집',
    'common.loading': '로딩 중...',
    'common.error': '오류',
    'common.retry': '다시 시도',
    'common.search': '검색',
    'common.back': '뒤로가기',
    'common.done': '완료',
    'common.change': '변경',
    'common.saved': '저장됨',
    'common.saving': '저장 중...',
    'common.copied': '복사됨!',
    'common.dismiss': '닫기',
    'common.sending': '전송 중...',
    'common.creating': '생성 중...',
    'common.opening': '여는 중...',

    // Profile Page
    'profile.title': '프로필',
    'profile.tag': '태그',
    'profile.displayName': '닉네임',
    'profile.displayNamePlaceholder': '예: 홍길동, Cook',
    'profile.username': '사용자명',
    'profile.usernamePlaceholder': '영문 소문자, 숫자만',
    'profile.status': '상태 메시지',
    'profile.statusPlaceholder': '지금 무슨 생각을 하고 있나요?',
    'profile.primaryLanguage': '언어',
    'profile.timezone': '시간대',
    'profile.selectTimezone': '시간대 선택',
    'profile.logout': '로그아웃',
    'profile.loggingOut': '로그아웃 중...',
    'profile.logoutConfirmTitle': '로그아웃',
    'profile.logoutConfirmMessage': '정말 로그아웃하시겠습니까?',
    'profile.changeUsernameTitle': '사용자명을 변경할까요?',
    'profile.changeUsernameMessage': '사용자명을 변경하면 새 태그가 생성됩니다.',
    'profile.saveChanges': '저장',

    // Settings Page
    'settings.title': '설정',
    'settings.notifications': '알림',
    'settings.pushNotifications': '푸시 알림',
    'settings.enableNotifications': '알림 활성화',
    'settings.disableNotifications': '알림 비활성화',
    'settings.notificationsBlocked': '브라우저 설정에서 알림이 차단되어 있습니다.',
    'settings.notificationsBlockedHelp': '브라우저 주소창의 자물쇠 아이콘 → 사이트 설정 → 알림 허용',
    'settings.privacy': '개인정보',
    'settings.blockedUsers': '차단된 사용자',
    'settings.app': '앱',
    'settings.installApp': '앱 설치',
    'settings.installAppDesc': 'Talksy를 앱으로 설치하여 빠르게 사용하세요',
    'settings.installAppManual': '브라우저 주소창의 설치 아이콘을 클릭하거나, 브라우저 메뉴 → "앱 설치"를 선택하세요',
    'settings.installing': '설치 중...',
    'settings.installed': '앱 설치됨',
    'settings.installedDesc': 'Talksy가 기기에 설치되어 있습니다',
    'settings.installGuideTitle': 'Talksy 설치',
    'settings.installStep1': '주소창의 설치 아이콘 클릭',
    'settings.installStep2': '또는 브라우저 메뉴에서 선택',
    'settings.installStep3': '설치 확인',
    'settings.installMobileTitle': '홈 화면에 추가',
    'settings.installMobileDesc': '최고의 경험을 위해 Talksy를 설치하세요',
    'settings.installIosStep1': '···',
    'settings.installIosSafariHint': '하단 우측 ···',
    'settings.installIosStep2': '공유',
    'settings.installIosStep3': '더보기',
    'settings.installIosStep4': '홈 화면에 추가',
    'settings.installIosStep5': '추가',
    'settings.installIosNote': 'Safari에서 설치하세요',
    'settings.installAndroidStep1': '메뉴(⋮) 버튼을 탭하세요',
    'settings.installAndroidStep2': '"홈 화면에 추가"를 탭하세요',
    'settings.installAndroidStep3': '"추가"를 탭하여 완료',
    'settings.installNow': '지금 설치',
    'settings.useNativeBrowser': '{browser}에서 열어서 설치하세요',

    // Friends Page
    'friends.title': '친구',
    'friends.addFriend': '친구 추가',
    'friends.pendingRequests': '대기 중인 요청',
    'friends.allFriends': '모든 친구',
    'friends.noFriends': '아직 친구가 없습니다',
    'friends.noFriendsDesc': '친구를 추가하고 대화를 시작해보세요',
    'friends.addFriendButton': '친구 추가하기',
    'friends.welcome': '안녕하세요, {name}님!',
    'friends.createChannel': '채널 만들기',
    'friends.copyTag': '태그 복사',
    'friends.sentRequest': '친구 요청을 보냈습니다',
    'friends.accept': '수락',
    'friends.decline': '거절',
    'friends.message': '메시지',
    'friends.requestSent': '요청 전송됨',
    'friends.requestReceived': '친구 요청 받음',
    'friends.friendsSince': '친구가 된 날짜',
    'friends.wantsToBeYourFriend': '친구가 되고 싶어합니다',
    'friends.acceptRequest': '요청 수락',
    'friends.requestedOn': '요청일',
    'friends.loadingFriends': '친구 목록을 불러오는 중...',

    // Add Friend Modal
    'addFriend.title': '친구 추가',
    'addFriend.subtitle': 'Talksy에서 인맥을 넓혀보세요',
    'addFriend.placeholder': 'Username#0000 입력',
    'addFriend.sendRequest': '친구 요청 보내기',
    'addFriend.errorEmpty': '태그를 입력해주세요',
    'addFriend.errorFormat': '올바른 형식이 아닙니다. Username#0000 형식으로 입력해주세요',
    'addFriend.errorSelf': '자신에게 친구 요청을 보낼 수 없습니다',
    'addFriend.errorNotFound': '해당 태그를 가진 사용자를 찾을 수 없습니다',
    'addFriend.errorAlreadyFriend': '이미 친구입니다',
    'addFriend.errorRequestSent': '이미 친구 요청을 보냈습니다',
    'addFriend.errorRequestReceived': '상대방이 이미 친구 요청을 보냈습니다. 친구 요청 탭을 확인해주세요',
    'addFriend.errorSendFailed': '친구 요청 전송에 실패했습니다',
    'addFriend.errorGeneral': '친구 요청 전송 중 오류가 발생했습니다',
    'addFriend.successMessage': '{name}님에게 친구 요청을 보냈습니다',

    // Create Channel Modal
    'createChannel.title': '채널 만들기',
    'createChannel.subtitle': '그룹을 위한 공간을 만드세요',
    'createChannel.channelName': '채널 이름',
    'createChannel.placeholder': '예: 게임 친구들',
    'createChannel.upload': '업로드',
    'createChannel.createButton': '채널 만들기',
    'createChannel.errorImageOnly': '이미지 파일만 업로드할 수 있습니다',
    'createChannel.errorFileSize': '파일 크기는 5MB 이하여야 합니다',
    'createChannel.errorUploadFailed': '아이콘 업로드에 실패했습니다',
    'createChannel.errorCreateFailed': '채널 생성에 실패했습니다',

    // Blocked Users
    'blockedUsers.title': '차단된 사용자',
    'blockedUsers.noBlockedUsers': '차단된 사용자 없음',
    'blockedUsers.noBlockedUsersDesc': '차단한 사용자가 여기에 표시됩니다',
    'blockedUsers.unblock': '차단 해제',
    'blockedUsers.unblockTitle': '사용자 차단 해제',
    'blockedUsers.unblockConfirm': '정말 차단을 해제하시겠습니까?',
    'blockedUsers.unblockInfo': '차단을 해제하면 상대방이 다시 친구 요청과 메시지를 보낼 수 있게 됩니다.',

    // Sidebar
    'sidebar.newConversation': '새 대화',
    'sidebar.addFriend': '친구 추가',
    'sidebar.addFriendDesc': '새 1:1 메시지',
    'sidebar.newChannel': '새 채널',
    'sidebar.newChannelDesc': '커뮤니티 만들기',

    // Network Banner
    'network.offline': '인터넷 연결이 끊어졌습니다',
    'network.reconnecting': '재연결 중...',
    'network.reconnect': '재연결',

    // Login Page
    'login.termsNotice': '로그인하면 서비스 이용약관 및 개인정보 처리방침에 동의하게 됩니다.',

    // Empty States
    'empty.conversations': '아직 대화가 없습니다',
    'empty.conversationsDesc': '친구와 대화를 시작해보세요',

    // Confirm Dialog
    'confirm.deleteTitle': '삭제',
    'confirm.blockTitle': '사용자 차단',

    // Friend Profile
    'friendProfile.inviteToChannel': '채널에 초대',
    'friendProfile.deleteFriend': '친구 삭제',
    'friendProfile.blockUser': '사용자 차단',
    'friendProfile.unblockUser': '차단 해제',
    'friendProfile.moreOptions': '더 보기',

    // Channel Members
    'channel.members.title': '채널 멤버',
    'channel.members.owner': '소유자',
    'channel.members.admin': '관리자',
    'channel.members.member': '멤버',
    'channel.members.me': '(나)',
    'channel.members.kick': '내보내기',
    'channel.members.kickConfirm': '{name}님을 채널에서 내보내시겠습니까?',
    'channel.members.transferOwnership': '소유권 이전',
    'channel.members.transferConfirm': '{name}님에게 채널 소유권을 이전하시겠습니까?\n\n이전 후에는 관리자로 변경됩니다.',
    'channel.members.ownerCount': '소유자 {count}',
    'channel.members.adminCount': '관리자 {count}',
    'channel.members.memberCount': '멤버 {count}',
    'channel.members.noMembers': '멤버가 없습니다',
    'channel.members.totalCount': '{count}명',

    // Channel Settings
    'channel.settings.title': '채널 설정',
    'channel.settings.name': '채널 이름',
    'channel.settings.namePlaceholder': '채널 이름을 입력하세요',
    'channel.settings.description': '설명',
    'channel.settings.descriptionPlaceholder': '채널에 대한 간단한 설명',
    'channel.settings.icon': '채널 아이콘',
    'channel.settings.iconChange': '클릭하여 변경',
    'channel.settings.save': '저장',
    'channel.settings.leave': '채널 나가기',
    'channel.settings.leaveWarning': '채널을 나가면 다시 초대받지 않는 한 재참여할 수 없습니다.',
    'channel.settings.leaveOwnerWarning': '채널 생성자는 다른 관리자가 있을 때만 나갈 수 있습니다.',
    'channel.settings.errorNameEmpty': '채널 이름을 입력하세요',
    'channel.settings.errorNameShort': '채널 이름은 최소 2자 이상이어야 합니다',
    'channel.settings.errorNameLong': '채널 이름은 최대 50자까지 가능합니다',
    'channel.settings.errorFileSize': '파일 크기는 5MB 이하여야 합니다',
    'channel.settings.errorUpload': '아이콘 업로드에 실패했습니다',
    'channel.settings.errorSave': '채널 정보 저장에 실패했습니다',
    'channel.settings.errorLeave': '채널 나가기에 실패했습니다',
    'channel.settings.errorOwnerLeave': '소유자는 소유권을 이전하거나 채널을 삭제해야 합니다',
    'channel.settings.errorNeedAdmin': '채널에는 최소 1명의 관리자가 필요합니다',

    // Channel Chat
    'channel.chat.messagePlaceholder': '#{name} 채널에 메시지',

    // Channel Invite
    'channel.invite.title': '채널 초대',
    'channel.invite.expiration': '만료 기간',
    'channel.invite.1h': '1시간',
    'channel.invite.1d': '1일',
    'channel.invite.7d': '7일',
    'channel.invite.never': '무제한',
    'channel.invite.generate': '초대 링크 생성',
    'channel.invite.copied': '클립보드에 복사됨!',
    'channel.invite.generated': '링크가 생성되었습니다',
    'channel.invite.copyAgain': '다시 복사하기',
    'channel.invite.noExpiry': '만료 기한 없음',
    'channel.invite.errorGenerate': '초대 링크 생성에 실패했습니다',
    'channel.invite.loginRequired': '로그인이 필요합니다',
    'channel.invite.loginRequiredDesc': '채널에 참여하려면 먼저 로그인해주세요.',
    'channel.invite.login': '로그인하기',
    'channel.invite.noCode': '초대 코드가 없습니다',
    'channel.invite.noCodeDesc': '유효한 초대 링크로 접속해주세요.',
    'channel.invite.goToChannels': '채널 목록으로',

    // Channel Errors
    'channel.error.notFound': '채널을 찾을 수 없습니다',
    'channel.error.noAccess': '이 채널에 접근할 권한이 없습니다',
    'channel.error.loadFailed': '채널을 불러오는데 실패했습니다',
    'channel.error.loginRequired': '로그인이 필요합니다',
    'channel.error.roleChangeFailed': '권한 변경에 실패했습니다',
    'channel.error.cannotManageOwner': '소유자의 권한은 변경할 수 없습니다',
    'channel.error.cannotManageAdmin': '관리자는 다른 관리자를 관리할 수 없습니다',
    'channel.error.transferFailed': '소유권 이전에 실패했습니다',
    'channel.error.kickFailed': '멤버 강퇴에 실패했습니다',
    'channel.error.kicked': '채널에서 강퇴되었습니다',
    'channel.error.needJoin': '메시지를 보내려면 채널에 참여해야 합니다',
    'channel.error.ownerCannotLeave': '소유자는 소유권을 이전하거나 채널을 삭제해야 합니다',

    // Channel Join
    'channel.join.join': '참여하기',
    'channel.join.cancel': '취소',
    'channel.join.goBack': '돌아가기',
    'channel.join.memberCount': '{count}명 참여 중',
    'channel.join.invalidLink': '유효하지 않은 초대 링크',
    'channel.join.invalidDesc': '이 초대 링크는 존재하지 않거나 삭제되었습니다.',
    'channel.join.usedLink': '이미 사용된 초대 링크',
    'channel.join.usedDesc': '이 초대 링크는 이미 다른 사람이 사용했습니다.',
    'channel.join.expiredLink': '만료된 초대 링크',
    'channel.join.expiredDesc': '이 초대 링크는 유효기간이 지났습니다.',
    'channel.join.selfLink': '본인의 초대 링크',
    'channel.join.selfDesc': '자신이 만든 초대 링크입니다. 채팅방으로 이동합니다.',
    'channel.join.maxUsed': '사용 횟수 초과',
    'channel.join.maxUsedDesc': '이 초대 링크의 사용 횟수가 초과되었습니다. 새로운 초대 링크를 요청해주세요.',
    'channel.join.alreadyMember': '이미 참여 중',
    'channel.join.alreadyMemberDesc': '이미 이 채널의 멤버입니다.',
    'channel.join.channelFull': '채널이 가득 참',
    'channel.join.channelFullDesc': '이 채널은 최대 인원에 도달했습니다.',
    'channel.join.notFoundLink': '초대 링크를 찾을 수 없음',
    'channel.join.notFoundDesc': '유효하지 않은 초대 링크입니다. 링크를 다시 확인해주세요.',

    // DM / Messages
    'dm.error.notFound': '대화를 찾을 수 없습니다',
    'dm.error.loadFailed': '대화를 불러오는데 실패했습니다',
    'dm.blocked.byMe': '이 사용자를 차단했습니다. 차단을 해제하면 메시지를 보낼 수 있습니다.',
    'dm.blocked.byThem': '메시지를 보낼 수 없습니다.',
    'dm.blocked.mutual': '차단된 사용자입니다. 메시지를 보내거나 받을 수 없습니다.',
    'dm.placeholder': '@{name}에게 메시지',
    'dm.placeholderDefault': '메시지를 입력하세요...',
    'dm.placeholderBlocked': '메시지를 보낼 수 없습니다',
    'dm.offline': '오프라인 상태입니다. 연결이 복구되면 메시지가 전송됩니다.',
    'dm.user': '사용자',

    // Time
    'time.justNow': '방금',
    'time.minuteAgo': '1분 전',
    'time.minutesAgo': '{count}분 전',
    'time.hourAgo': '1시간 전',
    'time.hoursAgo': '{count}시간 전',
    'time.yesterday': '어제',
    'time.dayAgo': '1일 전',
    'time.daysAgo': '{count}일 전',
    'time.requested': '요청됨 ',
    'time.sent': '보낸 날짜 ',

    // Language names (for profile display)
    'language.ko': '한국어',
    'language.en': '영어',
    'language.ja': '일본어',
    'language.zh': '중국어',
    'language.es': '스페인어',
    'language.fr': '프랑스어',
    'language.de': '독일어',
    'language.pt': '포르투갈어',
    'language.ru': '러시아어',
    'language.ar': '아랍어',
    'language.hi': '힌디어',
    'language.vi': '베트남어',
    'language.th': '태국어',
    'language.id': '인도네시아어',

    // System messages
    'system.memberJoined': '{name}님이 채널에 참여했습니다',
    'system.memberLeft': '{name}님이 채널을 떠났습니다',
    'system.memberKicked': '{name}님이 강퇴되었습니다',
    'system.channelUpdated': '채널 정보가 변경되었습니다',
    'system.roleChanged': '{name}님의 권한이 {role}(으)로 변경되었습니다',
    'system.ownershipTransferred': '채널 소유권이 {name}님에게 이전되었습니다',

    // Friend Requests
    'friendRequest.accept': '수락',
    'friendRequest.decline': '거절',
    'friendRequest.cancel': '요청 취소',
    'friendRequest.processing': '처리 중...',
    'friendRequest.acceptFailed': '수락에 실패했습니다',
    'friendRequest.declineFailed': '거절에 실패했습니다',
    'friendRequest.cancelFailed': '취소에 실패했습니다',
    'friendRequest.noReceived': '받은 요청이 없습니다',
    'friendRequest.noSent': '보낸 요청이 없습니다',
    'friendRequest.noReceivedDesc': '다른 사용자의 친구 요청이 여기에 표시됩니다',
    'friendRequest.noSentDesc': '보낸 친구 요청이 여기에 표시됩니다',

    // Search
    'search.placeholder': '태그 또는 사용자명으로 검색',
    'search.clear': '검색어 지우기',
    'search.noResults': '"{query}"에 해당하는 사용자를 찾을 수 없습니다',
    'search.tagHelp': '전체 태그(예: john#A1B2)를 입력하면 정확한 사용자를 찾을 수 있습니다',

    // Misc
    'misc.startConversation': '대화를 시작하세요',
    'misc.waitingForPartner': '상대방을 기다리는 중...',
    'misc.chat': '채팅',
    'misc.newConversation': '새 대화',
    'misc.waiting': '대기 중...',
    'misc.copyLink': '링크 복사하기',
    'misc.linkCopied': '복사 완료!',
    'misc.unknownError': '알 수 없는 오류가 발생했습니다',
    'misc.withdrawnUser': '탈퇴한 사용자',
    'misc.selectConversation': '대화를 선택하세요',
    'misc.selectConversationDesc': '왼쪽 목록에서 대화를 선택하거나 새로운 대화를 시작하세요.',
    'misc.selectChannel': '채널을 선택하세요',
    'misc.selectChannelDesc': '왼쪽 목록에서 채널을 선택하면 대화가 표시됩니다.',
  },

  // Japanese
  ja: {
    // Navigation
    'nav.friends': '友達',
    'nav.messages': 'メッセージ',
    'nav.profile': 'プロフィール',
    'nav.settings': '設定',

    // Common
    'common.save': '保存',
    'common.cancel': 'キャンセル',
    'common.close': '閉じる',
    'common.confirm': '確認',
    'common.delete': '削除',
    'common.edit': '編集',
    'common.loading': '読み込み中...',
    'common.error': 'エラー',
    'common.retry': '再試行',
    'common.search': '検索',
    'common.back': '戻る',
    'common.done': '完了',
    'common.change': '変更',
    'common.saved': '保存しました',
    'common.saving': '保存中...',
    'common.copied': 'コピーしました！',
    'common.dismiss': '閉じる',
    'common.sending': '送信中...',
    'common.creating': '作成中...',
    'common.opening': '開いています...',

    // Profile Page
    'profile.title': 'プロフィール',
    'profile.tag': 'タグ',
    'profile.displayName': 'ニックネーム',
    'profile.displayNamePlaceholder': '例: 田中、Cook',
    'profile.username': 'ユーザー名',
    'profile.usernamePlaceholder': '英小文字と数字のみ',
    'profile.status': 'ステータス',
    'profile.statusPlaceholder': '今何を考えていますか？',
    'profile.primaryLanguage': '言語',
    'profile.timezone': 'タイムゾーン',
    'profile.selectTimezone': 'タイムゾーンを選択',
    'profile.logout': 'ログアウト',
    'profile.loggingOut': 'ログアウト中...',
    'profile.logoutConfirmTitle': 'ログアウト',
    'profile.logoutConfirmMessage': '本当にログアウトしますか？',
    'profile.changeUsernameTitle': 'ユーザー名を変更しますか？',
    'profile.changeUsernameMessage': 'ユーザー名を変更すると新しいタグが生成されます。',
    'profile.saveChanges': '保存',

    // Settings Page
    'settings.title': '設定',
    'settings.notifications': '通知',
    'settings.pushNotifications': 'プッシュ通知',
    'settings.enableNotifications': '通知を有効にする',
    'settings.disableNotifications': '通知を無効にする',
    'settings.notificationsBlocked': 'ブラウザの設定で通知がブロックされています。',
    'settings.notificationsBlockedHelp': 'ブラウザのアドレスバーのロックアイコン → サイト設定 → 通知を許可',
    'settings.privacy': 'プライバシー',
    'settings.blockedUsers': 'ブロック中のユーザー',
    'settings.app': 'アプリ',
    'settings.installApp': 'アプリをインストール',
    'settings.installAppDesc': 'Talksyをアプリとしてインストールして素早くアクセス',
    'settings.installAppManual': 'ブラウザのアドレスバーにあるインストールアイコンをクリックするか、ブラウザメニュー→「アプリをインストール」を選択',
    'settings.installing': 'インストール中...',
    'settings.installed': 'アプリインストール済み',
    'settings.installedDesc': 'Talksyはデバイスにインストールされています',
    'settings.installGuideTitle': 'Talksyをインストール',
    'settings.installStep1': 'アドレスバーのアイコンをクリック',
    'settings.installStep2': 'またはブラウザメニューから選択',
    'settings.installStep3': 'インストールを確認',
    'settings.installMobileTitle': 'ホーム画面に追加',
    'settings.installMobileDesc': '最高の体験のためにTalksyをインストール',
    'settings.installIosStep1': '···',
    'settings.installIosSafariHint': '右下の···',
    'settings.installIosStep2': '共有',
    'settings.installIosStep3': 'その他',
    'settings.installIosStep4': 'ホーム画面に追加',
    'settings.installIosStep5': '追加',
    'settings.installIosNote': 'Safariでインストール',
    'settings.installAndroidStep1': 'メニュー(⋮)ボタンをタップ',
    'settings.installAndroidStep2': '「ホーム画面に追加」をタップ',
    'settings.installAndroidStep3': '「追加」をタップして完了',
    'settings.installNow': '今すぐインストール',
    'settings.useNativeBrowser': '{browser}で開いてインストール',

    // Friends Page
    'friends.title': '友達',
    'friends.addFriend': '友達を追加',
    'friends.pendingRequests': '保留中のリクエスト',
    'friends.allFriends': 'すべての友達',
    'friends.noFriends': 'まだ友達がいません',
    'friends.noFriendsDesc': '友達を追加してチャットを始めましょう',
    'friends.addFriendButton': '友達を追加する',
    'friends.welcome': 'ようこそ、{name}さん！',
    'friends.createChannel': 'チャンネルを作成',
    'friends.copyTag': 'タグをコピー',
    'friends.sentRequest': '友達リクエストを送りました',
    'friends.accept': '承認',
    'friends.decline': '拒否',
    'friends.message': 'メッセージ',
    'friends.requestSent': 'リクエスト送信済み',
    'friends.requestReceived': 'リクエストを受信',
    'friends.friendsSince': '友達になった日',
    'friends.wantsToBeYourFriend': '友達になりたいです',
    'friends.acceptRequest': 'リクエストを承認',
    'friends.requestedOn': 'リクエスト日',
    'friends.loadingFriends': '友達リストを読み込み中...',

    // Add Friend Modal
    'addFriend.title': '友達を追加',
    'addFriend.subtitle': 'Talksyで繋がりを広げよう',
    'addFriend.placeholder': 'Username#0000を入力',
    'addFriend.sendRequest': '友達リクエストを送信',
    'addFriend.errorEmpty': 'タグを入力してください',
    'addFriend.errorFormat': '形式が正しくありません。Username#0000形式で入力してください',
    'addFriend.errorSelf': '自分に友達リクエストは送れません',
    'addFriend.errorNotFound': 'このタグのユーザーが見つかりません',
    'addFriend.errorAlreadyFriend': 'すでに友達です',
    'addFriend.errorRequestSent': 'すでに友達リクエストを送信しています',
    'addFriend.errorRequestReceived': '相手からすでに友達リクエストが届いています。友達リクエストタブをご確認ください',
    'addFriend.errorSendFailed': '友達リクエストの送信に失敗しました',
    'addFriend.errorGeneral': '友達リクエストの送信中にエラーが発生しました',
    'addFriend.successMessage': '{name}さんに友達リクエストを送りました',

    // Create Channel Modal
    'createChannel.title': 'チャンネルを作成',
    'createChannel.subtitle': 'グループのスペースを作成',
    'createChannel.channelName': 'チャンネル名',
    'createChannel.placeholder': '例: ゲーム仲間',
    'createChannel.upload': 'アップロード',
    'createChannel.createButton': 'チャンネルを作成',
    'createChannel.errorImageOnly': '画像ファイルのみアップロードできます',
    'createChannel.errorFileSize': 'ファイルサイズは5MB以下にしてください',
    'createChannel.errorUploadFailed': 'アイコンのアップロードに失敗しました',
    'createChannel.errorCreateFailed': 'チャンネルの作成に失敗しました',

    // Blocked Users
    'blockedUsers.title': 'ブロック中のユーザー',
    'blockedUsers.noBlockedUsers': 'ブロック中のユーザーはいません',
    'blockedUsers.noBlockedUsersDesc': 'ブロックしたユーザーがここに表示されます',
    'blockedUsers.unblock': 'ブロック解除',
    'blockedUsers.unblockTitle': 'ユーザーのブロックを解除',
    'blockedUsers.unblockConfirm': '本当にブロックを解除しますか？',
    'blockedUsers.unblockInfo': 'ブロックを解除すると、相手は再び友達リクエストやメッセージを送れるようになります。',

    // Sidebar
    'sidebar.newConversation': '新しい会話',
    'sidebar.addFriend': '友達を追加',
    'sidebar.addFriendDesc': '新しいダイレクトメッセージ',
    'sidebar.newChannel': '新しいチャンネル',
    'sidebar.newChannelDesc': 'コミュニティを作成',

    // Network Banner
    'network.offline': 'インターネット接続が切断されました',
    'network.reconnecting': '再接続中...',
    'network.reconnect': '再接続',

    // Login Page
    'login.termsNotice': 'ログインすると、利用規約およびプライバシーポリシーに同意したことになります。',

    // Empty States
    'empty.conversations': 'まだ会話がありません',
    'empty.conversationsDesc': '友達と会話を始めましょう',

    // Confirm Dialog
    'confirm.deleteTitle': '削除',
    'confirm.blockTitle': 'ユーザーをブロック',

    // Friend Profile
    'friendProfile.inviteToChannel': 'チャンネルに招待',
    'friendProfile.deleteFriend': '友達を削除',
    'friendProfile.blockUser': 'ユーザーをブロック',
    'friendProfile.unblockUser': 'ブロック解除',
    'friendProfile.moreOptions': 'その他',

    // Channel Members
    'channel.members.title': 'チャンネルメンバー',
    'channel.members.owner': 'オーナー',
    'channel.members.admin': '管理者',
    'channel.members.member': 'メンバー',
    'channel.members.me': '(自分)',
    'channel.members.kick': '追放',
    'channel.members.kickConfirm': '{name}さんをチャンネルから追放しますか？',
    'channel.members.transferOwnership': '所有権を移転',
    'channel.members.transferConfirm': '{name}さんに所有権を移転しますか？\n\n移転後は管理者になります。',
    'channel.members.ownerCount': 'オーナー {count}',
    'channel.members.adminCount': '管理者 {count}',
    'channel.members.memberCount': 'メンバー {count}',
    'channel.members.noMembers': 'メンバーがいません',
    'channel.members.totalCount': '{count}人',

    // Channel Settings
    'channel.settings.title': 'チャンネル設定',
    'channel.settings.name': 'チャンネル名',
    'channel.settings.namePlaceholder': 'チャンネル名を入力',
    'channel.settings.description': '説明',
    'channel.settings.descriptionPlaceholder': 'チャンネルの簡単な説明',
    'channel.settings.icon': 'チャンネルアイコン',
    'channel.settings.iconChange': 'クリックして変更',
    'channel.settings.save': '保存',
    'channel.settings.leave': 'チャンネルを退出',
    'channel.settings.leaveWarning': '退出すると、再度招待されない限り参加できません。',
    'channel.settings.leaveOwnerWarning': 'オーナーは他の管理者がいる場合のみ退出できます。',
    'channel.settings.errorNameEmpty': 'チャンネル名を入力してください',
    'channel.settings.errorNameShort': 'チャンネル名は2文字以上必要です',
    'channel.settings.errorNameLong': 'チャンネル名は50文字以内です',
    'channel.settings.errorFileSize': 'ファイルサイズは5MB以下にしてください',
    'channel.settings.errorUpload': 'アイコンのアップロードに失敗しました',
    'channel.settings.errorSave': 'チャンネル設定の保存に失敗しました',
    'channel.settings.errorLeave': 'チャンネルの退出に失敗しました',
    'channel.settings.errorOwnerLeave': 'オーナーは所有権を移転するか、チャンネルを削除する必要があります',
    'channel.settings.errorNeedAdmin': 'チャンネルには最低1人の管理者が必要です',

    // Channel Invite
    // Channel Chat
    'channel.chat.messagePlaceholder': '#{name} へのメッセージ',

    'channel.invite.title': 'チャンネルに招待',
    'channel.invite.expiration': '有効期限',
    'channel.invite.1h': '1時間',
    'channel.invite.1d': '1日',
    'channel.invite.7d': '7日',
    'channel.invite.never': '無制限',
    'channel.invite.generate': '招待リンクを生成',
    'channel.invite.copied': 'クリップボードにコピーしました！',
    'channel.invite.generated': 'リンクが生成されました',
    'channel.invite.copyAgain': '再度コピー',
    'channel.invite.noExpiry': '有効期限なし',
    'channel.invite.errorGenerate': '招待リンクの生成に失敗しました',
    'channel.invite.loginRequired': 'ログインが必要です',
    'channel.invite.loginRequiredDesc': 'チャンネルに参加するにはログインしてください。',
    'channel.invite.login': 'ログインする',
    'channel.invite.noCode': '招待コードがありません',
    'channel.invite.noCodeDesc': '有効な招待リンクからアクセスしてください。',
    'channel.invite.goToChannels': 'チャンネル一覧へ',

    // Channel Errors
    'channel.error.notFound': 'チャンネルが見つかりません',
    'channel.error.noAccess': 'このチャンネルへのアクセス権がありません',
    'channel.error.loadFailed': 'チャンネルの読み込みに失敗しました',
    'channel.error.loginRequired': 'ログインが必要です',
    'channel.error.roleChangeFailed': '権限の変更に失敗しました',
    'channel.error.cannotManageOwner': 'オーナーの権限は変更できません',
    'channel.error.cannotManageAdmin': '管理者は他の管理者を管理できません',
    'channel.error.transferFailed': '所有権の移転に失敗しました',
    'channel.error.kickFailed': 'メンバーの追放に失敗しました',
    'channel.error.kicked': 'チャンネルから追放されました',
    'channel.error.needJoin': 'メッセージを送信するにはチャンネルに参加する必要があります',
    'channel.error.ownerCannotLeave': 'オーナーは所有権を移転するかチャンネルを削除する必要があります',

    // Channel Join
    'channel.join.join': '参加する',
    'channel.join.cancel': 'キャンセル',
    'channel.join.goBack': '戻る',
    'channel.join.memberCount': '{count}人が参加中',
    'channel.join.invalidLink': '無効な招待リンク',
    'channel.join.invalidDesc': 'この招待リンクは存在しないか、削除されました。',
    'channel.join.usedLink': '使用済みの招待リンク',
    'channel.join.usedDesc': 'この招待リンクは既に使用されています。',
    'channel.join.expiredLink': '期限切れの招待リンク',
    'channel.join.expiredDesc': 'この招待リンクは有効期限が切れています。',
    'channel.join.selfLink': '自分の招待リンク',
    'channel.join.selfDesc': '自分が作成した招待リンクです。チャットルームに移動します。',
    'channel.join.maxUsed': '使用回数超過',
    'channel.join.maxUsedDesc': 'この招待リンクの使用回数が上限に達しました。新しい招待リンクをリクエストしてください。',
    'channel.join.alreadyMember': '既に参加済み',
    'channel.join.alreadyMemberDesc': '既にこのチャンネルのメンバーです。',
    'channel.join.channelFull': 'チャンネルが満員',
    'channel.join.channelFullDesc': 'このチャンネルは最大人数に達しています。',
    'channel.join.notFoundLink': '招待リンクが見つかりません',
    'channel.join.notFoundDesc': '無効な招待リンクです。リンクを再度確認してください。',

    // DM / Messages
    'dm.error.notFound': '会話が見つかりません',
    'dm.error.loadFailed': '会話の読み込みに失敗しました',
    'dm.blocked.byMe': 'このユーザーをブロックしています。ブロックを解除するとメッセージを送信できます。',
    'dm.blocked.byThem': 'メッセージを送信できません。',
    'dm.blocked.mutual': 'ブロックされたユーザーです。メッセージの送受信ができません。',
    'dm.placeholder': '@{name}にメッセージ',
    'dm.placeholderDefault': 'メッセージを入力...',
    'dm.placeholderBlocked': 'メッセージを送信できません',
    'dm.offline': 'オフラインです。接続が復旧すればメッセージが送信されます。',
    'dm.user': 'ユーザー',

    // Time
    'time.justNow': 'たった今',
    'time.minuteAgo': '1分前',
    'time.minutesAgo': '{count}分前',
    'time.hourAgo': '1時間前',
    'time.hoursAgo': '{count}時間前',
    'time.yesterday': '昨日',
    'time.dayAgo': '1日前',
    'time.daysAgo': '{count}日前',
    'time.requested': 'リクエスト日 ',
    'time.sent': '送信日 ',

    // Language names (for profile display)
    'language.ko': '韓国語',
    'language.en': '英語',
    'language.ja': '日本語',
    'language.zh': '中国語',
    'language.es': 'スペイン語',
    'language.fr': 'フランス語',
    'language.de': 'ドイツ語',
    'language.pt': 'ポルトガル語',
    'language.ru': 'ロシア語',
    'language.ar': 'アラビア語',
    'language.hi': 'ヒンディー語',
    'language.vi': 'ベトナム語',
    'language.th': 'タイ語',
    'language.id': 'インドネシア語',

    // System messages
    'system.memberJoined': '{name}さんがチャンネルに参加しました',
    'system.memberLeft': '{name}さんがチャンネルを退出しました',
    'system.memberKicked': '{name}さんが追放されました',
    'system.channelUpdated': 'チャンネル情報が更新されました',
    'system.roleChanged': '{name}さんの権限が{role}に変更されました',
    'system.ownershipTransferred': 'チャンネルの所有権が{name}さんに移転されました',

    // Friend Requests
    'friendRequest.accept': '承認',
    'friendRequest.decline': '拒否',
    'friendRequest.cancel': 'リクエスト取消',
    'friendRequest.processing': '処理中...',
    'friendRequest.acceptFailed': '承認に失敗しました',
    'friendRequest.declineFailed': '拒否に失敗しました',
    'friendRequest.cancelFailed': 'キャンセルに失敗しました',
    'friendRequest.noReceived': '受信したリクエストはありません',
    'friendRequest.noSent': '送信したリクエストはありません',
    'friendRequest.noReceivedDesc': '他のユーザーからの友達リクエストがここに表示されます',
    'friendRequest.noSentDesc': '送信した友達リクエストがここに表示されます',

    // Search
    'search.placeholder': 'タグまたはユーザー名で検索',
    'search.clear': '検索をクリア',
    'search.noResults': '「{query}」に該当するユーザーが見つかりません',
    'search.tagHelp': '完全なタグ（例: john#A1B2）を入力すると正確なユーザーを検索できます',

    // Misc
    'misc.startConversation': '会話を始める',
    'misc.waitingForPartner': '相手を待っています...',
    'misc.chat': 'チャット',
    'misc.newConversation': '新しい会話',
    'misc.waiting': '待機中...',
    'misc.copyLink': 'リンクをコピー',
    'misc.linkCopied': 'コピーしました！',
    'misc.unknownError': '不明なエラーが発生しました',
    'misc.withdrawnUser': '退会したユーザー',
    'misc.selectConversation': '会話を選択',
    'misc.selectConversationDesc': 'リストから会話を選択するか、新しい会話を始めてください。',
    'misc.selectChannel': 'チャンネルを選択',
    'misc.selectChannelDesc': 'リストからチャンネルを選択すると会話が表示されます。',
  },

  // Chinese
  zh: {
    // Navigation
    'nav.friends': '好友',
    'nav.messages': '消息',
    'nav.profile': '个人资料',
    'nav.settings': '设置',

    // Common
    'common.save': '保存',
    'common.cancel': '取消',
    'common.close': '关闭',
    'common.confirm': '确认',
    'common.delete': '删除',
    'common.edit': '编辑',
    'common.loading': '加载中...',
    'common.error': '错误',
    'common.retry': '重试',
    'common.search': '搜索',
    'common.back': '返回',
    'common.done': '完成',
    'common.change': '更改',
    'common.saved': '已保存',
    'common.saving': '保存中...',
    'common.copied': '已复制！',
    'common.dismiss': '关闭',
    'common.sending': '发送中...',
    'common.creating': '创建中...',
    'common.opening': '打开中...',

    // Profile Page
    'profile.title': '个人资料',
    'profile.tag': '标签',
    'profile.displayName': '昵称',
    'profile.displayNamePlaceholder': '例如: 小明, Cook',
    'profile.username': '用户名',
    'profile.usernamePlaceholder': '仅限小写字母和数字',
    'profile.status': '状态',
    'profile.statusPlaceholder': '你在想什么？',
    'profile.primaryLanguage': '语言',
    'profile.timezone': '时区',
    'profile.selectTimezone': '选择时区',
    'profile.logout': '退出登录',
    'profile.loggingOut': '退出中...',
    'profile.logoutConfirmTitle': '退出登录',
    'profile.logoutConfirmMessage': '确定要退出登录吗？',
    'profile.changeUsernameTitle': '要更改用户名吗？',
    'profile.changeUsernameMessage': '更改用户名将生成新的标签。',
    'profile.saveChanges': '保存',

    // Settings Page
    'settings.title': '设置',
    'settings.notifications': '通知',
    'settings.pushNotifications': '推送通知',
    'settings.enableNotifications': '启用通知',
    'settings.disableNotifications': '禁用通知',
    'settings.notificationsBlocked': '浏览器设置中已阻止通知。',
    'settings.notificationsBlockedHelp': '点击浏览器地址栏中的锁图标 → 网站设置 → 允许通知',
    'settings.privacy': '隐私',
    'settings.blockedUsers': '已屏蔽用户',
    'settings.app': '应用',
    'settings.installApp': '安装应用',
    'settings.installAppDesc': '将Talksy安装为应用，方便快速访问',
    'settings.installAppManual': '点击浏览器地址栏中的安装图标，或使用浏览器菜单→"安装应用"',
    'settings.installing': '安装中...',
    'settings.installed': '已安装应用',
    'settings.installedDesc': 'Talksy已安装在您的设备上',
    'settings.installGuideTitle': '安装Talksy',
    'settings.installStep1': '点击地址栏的安装图标',
    'settings.installStep2': '或从浏览器菜单选择',
    'settings.installStep3': '确认安装',
    'settings.installMobileTitle': '添加到主屏幕',
    'settings.installMobileDesc': '安装Talksy获得最佳体验',
    'settings.installIosStep1': '···',
    'settings.installIosSafariHint': '右下角···',
    'settings.installIosStep2': '共享',
    'settings.installIosStep3': '更多',
    'settings.installIosStep4': '添加到主屏幕',
    'settings.installIosStep5': '添加',
    'settings.installIosNote': '使用Safari安装',
    'settings.installAndroidStep1': '点击菜单(⋮)按钮',
    'settings.installAndroidStep2': '点击"添加到主屏幕"',
    'settings.installAndroidStep3': '点击"添加"确认',
    'settings.installNow': '立即安装',
    'settings.useNativeBrowser': '在{browser}中打开以安装',

    // Friends Page
    'friends.title': '好友',
    'friends.addFriend': '添加好友',
    'friends.pendingRequests': '待处理请求',
    'friends.allFriends': '所有好友',
    'friends.noFriends': '还没有好友',
    'friends.noFriendsDesc': '添加好友并开始聊天',
    'friends.addFriendButton': '添加好友',
    'friends.welcome': '欢迎，{name}！',
    'friends.createChannel': '创建频道',
    'friends.copyTag': '复制标签',
    'friends.sentRequest': '向你发送了好友请求',
    'friends.accept': '接受',
    'friends.decline': '拒绝',
    'friends.message': '发消息',
    'friends.requestSent': '已发送请求',
    'friends.requestReceived': '收到请求',
    'friends.friendsSince': '成为好友于',
    'friends.wantsToBeYourFriend': '想成为你的好友',
    'friends.acceptRequest': '接受请求',
    'friends.requestedOn': '请求日期',
    'friends.loadingFriends': '加载好友列表中...',

    // Add Friend Modal
    'addFriend.title': '添加好友',
    'addFriend.subtitle': '在Talksy扩展你的社交圈',
    'addFriend.placeholder': '输入 Username#0000',
    'addFriend.sendRequest': '发送好友请求',
    'addFriend.errorEmpty': '请输入标签',
    'addFriend.errorFormat': '格式不正确。请使用 Username#0000 格式',
    'addFriend.errorSelf': '不能向自己发送好友请求',
    'addFriend.errorNotFound': '未找到此标签的用户',
    'addFriend.errorAlreadyFriend': '已经是好友了',
    'addFriend.errorRequestSent': '已发送好友请求',
    'addFriend.errorRequestReceived': '对方已向你发送好友请求。请查看好友请求标签页',
    'addFriend.errorSendFailed': '发送好友请求失败',
    'addFriend.errorGeneral': '发送好友请求时出错',
    'addFriend.successMessage': '已向 {name} 发送好友请求',

    // Create Channel Modal
    'createChannel.title': '创建频道',
    'createChannel.subtitle': '为您的群组创建空间',
    'createChannel.channelName': '频道名称',
    'createChannel.placeholder': '例如: 游戏小队',
    'createChannel.upload': '上传',
    'createChannel.createButton': '创建频道',
    'createChannel.errorImageOnly': '只能上传图片文件',
    'createChannel.errorFileSize': '文件大小不能超过5MB',
    'createChannel.errorUploadFailed': '上传图标失败',
    'createChannel.errorCreateFailed': '创建频道失败',

    // Blocked Users
    'blockedUsers.title': '已屏蔽用户',
    'blockedUsers.noBlockedUsers': '没有已屏蔽的用户',
    'blockedUsers.noBlockedUsersDesc': '你屏蔽的用户将显示在这里',
    'blockedUsers.unblock': '取消屏蔽',
    'blockedUsers.unblockTitle': '取消屏蔽用户',
    'blockedUsers.unblockConfirm': '确定要取消屏蔽吗？',
    'blockedUsers.unblockInfo': '取消屏蔽后，对方将能够再次向你发送好友请求和消息。',

    // Sidebar
    'sidebar.newConversation': '新对话',
    'sidebar.addFriend': '添加好友',
    'sidebar.addFriendDesc': '新私信',
    'sidebar.newChannel': '新频道',
    'sidebar.newChannelDesc': '创建社区',

    // Network Banner
    'network.offline': '网络连接已断开',
    'network.reconnecting': '重新连接中...',
    'network.reconnect': '重新连接',

    // Login Page
    'login.termsNotice': '登录即表示您同意我们的服务条款和隐私政策。',

    // Empty States
    'empty.conversations': '还没有对话',
    'empty.conversationsDesc': '开始与好友聊天',

    // Confirm Dialog
    'confirm.deleteTitle': '删除',
    'confirm.blockTitle': '屏蔽用户',

    // Friend Profile
    'friendProfile.inviteToChannel': '邀请加入频道',
    'friendProfile.deleteFriend': '删除好友',
    'friendProfile.blockUser': '屏蔽用户',
    'friendProfile.unblockUser': '取消屏蔽',
    'friendProfile.moreOptions': '更多选项',

    // Channel Members
    'channel.members.title': '频道成员',
    'channel.members.owner': '所有者',
    'channel.members.admin': '管理员',
    'channel.members.member': '成员',
    'channel.members.me': '(我)',
    'channel.members.kick': '踢出',
    'channel.members.kickConfirm': '确定要将 {name} 从频道中移除吗？',
    'channel.members.transferOwnership': '转让所有权',
    'channel.members.transferConfirm': '确定要将所有权转让给 {name} 吗？\n\n转让后您将成为管理员。',
    'channel.members.ownerCount': '所有者 {count}',
    'channel.members.adminCount': '管理员 {count}',
    'channel.members.memberCount': '成员 {count}',
    'channel.members.noMembers': '没有成员',
    'channel.members.totalCount': '{count}人',

    // Channel Settings
    'channel.settings.title': '频道设置',
    'channel.settings.name': '频道名称',
    'channel.settings.namePlaceholder': '输入频道名称',
    'channel.settings.description': '描述',
    'channel.settings.descriptionPlaceholder': '频道的简短描述',
    'channel.settings.icon': '频道图标',
    'channel.settings.iconChange': '点击更改',
    'channel.settings.save': '保存',
    'channel.settings.leave': '退出频道',
    'channel.settings.leaveWarning': '退出后，除非再次被邀请，否则无法重新加入。',
    'channel.settings.leaveOwnerWarning': '作为所有者，只有在有其他管理员时才能退出。',
    'channel.settings.errorNameEmpty': '请输入频道名称',
    'channel.settings.errorNameShort': '频道名称至少需要2个字符',
    'channel.settings.errorNameLong': '频道名称最多50个字符',
    'channel.settings.errorFileSize': '文件大小不能超过5MB',
    'channel.settings.errorUpload': '上传图标失败',
    'channel.settings.errorSave': '保存频道设置失败',
    'channel.settings.errorLeave': '退出频道失败',
    'channel.settings.errorOwnerLeave': '所有者必须转让所有权或删除频道',
    'channel.settings.errorNeedAdmin': '频道至少需要1名管理员',

    // Channel Invite
    // Channel Chat
    'channel.chat.messagePlaceholder': '#{name} 发送消息',

    'channel.invite.title': '邀请加入频道',
    'channel.invite.expiration': '有效期',
    'channel.invite.1h': '1小时',
    'channel.invite.1d': '1天',
    'channel.invite.7d': '7天',
    'channel.invite.never': '永不过期',
    'channel.invite.generate': '生成邀请链接',
    'channel.invite.copied': '已复制到剪贴板！',
    'channel.invite.generated': '链接已生成',
    'channel.invite.copyAgain': '再次复制',
    'channel.invite.noExpiry': '永不过期',
    'channel.invite.errorGenerate': '生成邀请链接失败',
    'channel.invite.loginRequired': '需要登录',
    'channel.invite.loginRequiredDesc': '请登录以加入频道。',
    'channel.invite.login': '登录',
    'channel.invite.noCode': '没有邀请码',
    'channel.invite.noCodeDesc': '请通过有效的邀请链接访问。',
    'channel.invite.goToChannels': '前往频道列表',

    // Channel Errors
    'channel.error.notFound': '找不到频道',
    'channel.error.noAccess': '您没有访问此频道的权限',
    'channel.error.loadFailed': '加载频道失败',
    'channel.error.loginRequired': '需要登录',
    'channel.error.roleChangeFailed': '更改权限失败',
    'channel.error.cannotManageOwner': '无法更改所有者的权限',
    'channel.error.cannotManageAdmin': '管理员无法管理其他管理员',
    'channel.error.transferFailed': '转让所有权失败',
    'channel.error.kickFailed': '踢出成员失败',
    'channel.error.kicked': '您已被踢出频道',
    'channel.error.needJoin': '需要加入频道才能发送消息',
    'channel.error.ownerCannotLeave': '所有者必须转让所有权或删除频道',

    // Channel Join
    'channel.join.join': '加入',
    'channel.join.cancel': '取消',
    'channel.join.goBack': '返回',
    'channel.join.memberCount': '{count}人参与中',
    'channel.join.invalidLink': '无效的邀请链接',
    'channel.join.invalidDesc': '此邀请链接不存在或已被删除。',
    'channel.join.usedLink': '已使用的邀请链接',
    'channel.join.usedDesc': '此邀请链接已被其他人使用。',
    'channel.join.expiredLink': '已过期的邀请链接',
    'channel.join.expiredDesc': '此邀请链接已过期。',
    'channel.join.selfLink': '您自己的邀请链接',
    'channel.join.selfDesc': '这是您自己创建的邀请链接。正在跳转到聊天室。',
    'channel.join.maxUsed': '使用次数已达上限',
    'channel.join.maxUsedDesc': '此邀请链接的使用次数已达上限。请请求新的邀请链接。',
    'channel.join.alreadyMember': '已是成员',
    'channel.join.alreadyMemberDesc': '您已经是此频道的成员。',
    'channel.join.channelFull': '频道已满',
    'channel.join.channelFullDesc': '此频道已达到最大人数。',
    'channel.join.notFoundLink': '找不到邀请链接',
    'channel.join.notFoundDesc': '无效的邀请链接。请重新检查链接。',

    // DM / Messages
    'dm.error.notFound': '找不到对话',
    'dm.error.loadFailed': '加载对话失败',
    'dm.blocked.byMe': '您已屏蔽此用户。取消屏蔽后可以发送消息。',
    'dm.blocked.byThem': '无法发送消息。',
    'dm.blocked.mutual': '已屏蔽的用户。无法发送或接收消息。',
    'dm.placeholder': '发消息给 @{name}',
    'dm.placeholderDefault': '输入消息...',
    'dm.placeholderBlocked': '无法发送消息',
    'dm.offline': '您当前处于离线状态。连接恢复后消息将被发送。',
    'dm.user': '用户',

    // Time
    'time.justNow': '刚刚',
    'time.minuteAgo': '1分钟前',
    'time.minutesAgo': '{count}分钟前',
    'time.hourAgo': '1小时前',
    'time.hoursAgo': '{count}小时前',
    'time.yesterday': '昨天',
    'time.dayAgo': '1天前',
    'time.daysAgo': '{count}天前',
    'time.requested': '请求于 ',
    'time.sent': '发送于 ',

    // Language names (for profile display)
    'language.ko': '韩语',
    'language.en': '英语',
    'language.ja': '日语',
    'language.zh': '中文',
    'language.es': '西班牙语',
    'language.fr': '法语',
    'language.de': '德语',
    'language.pt': '葡萄牙语',
    'language.ru': '俄语',
    'language.ar': '阿拉伯语',
    'language.hi': '印地语',
    'language.vi': '越南语',
    'language.th': '泰语',
    'language.id': '印尼语',

    // System messages
    'system.memberJoined': '{name} 加入了频道',
    'system.memberLeft': '{name} 离开了频道',
    'system.memberKicked': '{name} 被踢出',
    'system.channelUpdated': '频道信息已更新',
    'system.roleChanged': '{name} 的权限已更改为 {role}',
    'system.ownershipTransferred': '频道所有权已转移给 {name}',

    // Friend Requests
    'friendRequest.accept': '接受',
    'friendRequest.decline': '拒绝',
    'friendRequest.cancel': '取消请求',
    'friendRequest.processing': '处理中...',
    'friendRequest.acceptFailed': '接受失败',
    'friendRequest.declineFailed': '拒绝失败',
    'friendRequest.cancelFailed': '取消失败',
    'friendRequest.noReceived': '没有收到的请求',
    'friendRequest.noSent': '没有发送的请求',
    'friendRequest.noReceivedDesc': '其他用户的好友请求将显示在这里',
    'friendRequest.noSentDesc': '您发送的好友请求将显示在这里',

    // Search
    'search.placeholder': '按标签或用户名搜索',
    'search.clear': '清除搜索',
    'search.noResults': '未找到"{query}"相关的用户',
    'search.tagHelp': '输入完整标签（如 john#A1B2）可以精确查找用户',

    // Misc
    'misc.startConversation': '开始对话',
    'misc.waitingForPartner': '等待对方...',
    'misc.chat': '聊天',
    'misc.newConversation': '新对话',
    'misc.waiting': '等待中...',
    'misc.copyLink': '复制链接',
    'misc.linkCopied': '已复制！',
    'misc.unknownError': '发生未知错误',
    'misc.withdrawnUser': '已注销用户',
    'misc.selectConversation': '选择对话',
    'misc.selectConversationDesc': '从列表中选择对话或开始新对话。',
    'misc.selectChannel': '选择频道',
    'misc.selectChannelDesc': '从列表中选择频道以查看对话。',
  },

  // Spanish
  es: {
    // Navigation
    'nav.friends': 'Amigos',
    'nav.messages': 'Mensajes',
    'nav.profile': 'Perfil',
    'nav.settings': 'Configuración',

    // Common
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.close': 'Cerrar',
    'common.confirm': 'Confirmar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.retry': 'Reintentar',
    'common.search': 'Buscar',
    'common.back': 'Volver',
    'common.done': 'Hecho',
    'common.change': 'Cambiar',
    'common.saved': 'Guardado',
    'common.saving': 'Guardando...',
    'common.copied': '¡Copiado!',
    'common.dismiss': 'Cerrar',
    'common.sending': 'Enviando...',
    'common.creating': 'Creando...',
    'common.opening': 'Abriendo...',

    // Profile Page
    'profile.title': 'Perfil',
    'profile.tag': 'Etiqueta',
    'profile.displayName': 'Apodo',
    'profile.displayNamePlaceholder': 'ej: Juan, Cook',
    'profile.username': 'Usuario',
    'profile.usernamePlaceholder': 'solo minúsculas y números',
    'profile.status': 'Estado',
    'profile.statusPlaceholder': '¿En qué estás pensando?',
    'profile.primaryLanguage': 'Idioma',
    'profile.timezone': 'Zona horaria',
    'profile.selectTimezone': 'Seleccionar zona horaria',
    'profile.logout': 'Cerrar sesión',
    'profile.loggingOut': 'Cerrando sesión...',
    'profile.logoutConfirmTitle': 'Cerrar sesión',
    'profile.logoutConfirmMessage': '¿Estás seguro de que quieres cerrar sesión?',
    'profile.changeUsernameTitle': '¿Cambiar usuario?',
    'profile.changeUsernameMessage': 'Cambiar tu usuario generará una nueva etiqueta.',
    'profile.saveChanges': 'Guardar',

    // Settings Page
    'settings.title': 'Configuración',
    'settings.notifications': 'Notificaciones',
    'settings.pushNotifications': 'Notificaciones push',
    'settings.enableNotifications': 'Activar notificaciones',
    'settings.disableNotifications': 'Desactivar notificaciones',
    'settings.notificationsBlocked': 'Las notificaciones están bloqueadas en la configuración del navegador.',
    'settings.notificationsBlockedHelp': 'Haz clic en el icono del candado en la barra de direcciones → Configuración del sitio → Permitir notificaciones',
    'settings.privacy': 'Privacidad',
    'settings.blockedUsers': 'Usuarios bloqueados',
    'settings.app': 'Aplicación',
    'settings.installApp': 'Instalar aplicación',
    'settings.installAppDesc': 'Instala Talksy como aplicación para acceso rápido',
    'settings.installAppManual': 'Haz clic en el icono de instalación en la barra de direcciones, o usa el menú del navegador → "Instalar aplicación"',
    'settings.installing': 'Instalando...',
    'settings.installed': 'Aplicación instalada',
    'settings.installedDesc': 'Talksy está instalado en tu dispositivo',
    'settings.installGuideTitle': 'Instalar Talksy',
    'settings.installStep1': 'Clic en el icono de la barra de direcciones',
    'settings.installStep2': 'O selecciona del menú del navegador',
    'settings.installStep3': 'Confirmar instalación',
    'settings.installMobileTitle': 'Añadir a pantalla de inicio',
    'settings.installMobileDesc': 'Instala Talksy para la mejor experiencia',
    'settings.installIosStep1': '···',
    'settings.installIosSafariHint': 'Abajo derecha ···',
    'settings.installIosStep2': 'Compartir',
    'settings.installIosStep3': 'Más',
    'settings.installIosStep4': 'Añadir a inicio',
    'settings.installIosStep5': 'Añadir',
    'settings.installIosNote': 'Usa Safari para instalar',
    'settings.installAndroidStep1': 'Toca el botón de menú (⋮)',
    'settings.installAndroidStep2': 'Toca "Añadir a pantalla de inicio"',
    'settings.installAndroidStep3': 'Toca "Añadir" para confirmar',
    'settings.installNow': 'Instalar ahora',
    'settings.useNativeBrowser': 'Abre en {browser} para instalar',

    // Friends Page
    'friends.title': 'Amigos',
    'friends.addFriend': 'Añadir amigo',
    'friends.pendingRequests': 'Solicitudes pendientes',
    'friends.allFriends': 'Todos los amigos',
    'friends.noFriends': 'Aún no tienes amigos',
    'friends.noFriendsDesc': 'Añade amigos y empieza a chatear',
    'friends.addFriendButton': 'Añadir amigo',
    'friends.welcome': '¡Bienvenido, {name}!',
    'friends.createChannel': 'Crear canal',
    'friends.copyTag': 'Copiar etiqueta',
    'friends.sentRequest': 'Te envió una solicitud de amistad',
    'friends.accept': 'Aceptar',
    'friends.decline': 'Rechazar',
    'friends.message': 'Mensaje',
    'friends.requestSent': 'Solicitud enviada',
    'friends.requestReceived': 'Solicitud recibida',
    'friends.friendsSince': 'Amigos desde',
    'friends.wantsToBeYourFriend': 'Quiere ser tu amigo',
    'friends.acceptRequest': 'Aceptar solicitud',
    'friends.requestedOn': 'Solicitado el',
    'friends.loadingFriends': 'Cargando lista de amigos...',

    // Add Friend Modal
    'addFriend.title': 'Añadir amigo',
    'addFriend.subtitle': 'Amplía tu círculo en Talksy',
    'addFriend.placeholder': 'Introduce Username#0000',
    'addFriend.sendRequest': 'Enviar solicitud de amistad',
    'addFriend.errorEmpty': 'Por favor introduce una etiqueta',
    'addFriend.errorFormat': 'Formato no válido. Usa el formato Username#0000',
    'addFriend.errorSelf': 'No puedes enviarte una solicitud de amistad a ti mismo',
    'addFriend.errorNotFound': 'Usuario con esta etiqueta no encontrado',
    'addFriend.errorAlreadyFriend': 'Ya sois amigos',
    'addFriend.errorRequestSent': 'Solicitud de amistad ya enviada',
    'addFriend.errorRequestReceived': 'Este usuario ya te ha enviado una solicitud. Revisa la pestaña de solicitudes',
    'addFriend.errorSendFailed': 'Error al enviar la solicitud de amistad',
    'addFriend.errorGeneral': 'Ocurrió un error al enviar la solicitud',
    'addFriend.successMessage': 'Solicitud de amistad enviada a {name}',

    // Create Channel Modal
    'createChannel.title': 'Crear canal',
    'createChannel.subtitle': 'Crea un espacio para tu grupo',
    'createChannel.channelName': 'NOMBRE DEL CANAL',
    'createChannel.placeholder': 'ej: Grupo de Gaming',
    'createChannel.upload': 'Subir',
    'createChannel.createButton': 'Crear canal',
    'createChannel.errorImageOnly': 'Solo se pueden subir archivos de imagen',
    'createChannel.errorFileSize': 'El archivo debe ser de 5MB o menos',
    'createChannel.errorUploadFailed': 'Error al subir el icono',
    'createChannel.errorCreateFailed': 'Error al crear el canal',

    // Blocked Users
    'blockedUsers.title': 'Usuarios bloqueados',
    'blockedUsers.noBlockedUsers': 'No hay usuarios bloqueados',
    'blockedUsers.noBlockedUsersDesc': 'Los usuarios que bloquees aparecerán aquí',
    'blockedUsers.unblock': 'Desbloquear',
    'blockedUsers.unblockTitle': 'Desbloquear usuario',
    'blockedUsers.unblockConfirm': '¿Seguro que quieres desbloquear a',
    'blockedUsers.unblockInfo': 'Podrá enviarte solicitudes de amistad y mensajes de nuevo.',

    // Sidebar
    'sidebar.newConversation': 'Nueva conversación',
    'sidebar.addFriend': 'Añadir amigo',
    'sidebar.addFriendDesc': 'Nuevo mensaje directo',
    'sidebar.newChannel': 'Nuevo canal',
    'sidebar.newChannelDesc': 'Crear una comunidad',

    // Network Banner
    'network.offline': 'Conexión a internet perdida',
    'network.reconnecting': 'Reconectando...',
    'network.reconnect': 'Reconectar',

    // Login Page
    'login.termsNotice': 'Al iniciar sesión, aceptas nuestros Términos de Servicio y Política de Privacidad.',

    // Empty States
    'empty.conversations': 'Aún no hay conversaciones',
    'empty.conversationsDesc': 'Empieza una conversación con amigos',

    // Confirm Dialog
    'confirm.deleteTitle': 'Eliminar',
    'confirm.blockTitle': 'Bloquear usuario',

    // Friend Profile
    'friendProfile.inviteToChannel': 'Invitar al canal',
    'friendProfile.deleteFriend': 'Eliminar amigo',
    'friendProfile.blockUser': 'Bloquear usuario',
    'friendProfile.unblockUser': 'Desbloquear usuario',
    'friendProfile.moreOptions': 'Más opciones',

    // Channel Members
    'channel.members.title': 'Miembros del canal',
    'channel.members.owner': 'Propietario',
    'channel.members.admin': 'Administrador',
    'channel.members.member': 'Miembro',
    'channel.members.me': '(yo)',
    'channel.members.kick': 'Expulsar',
    'channel.members.kickConfirm': '¿Eliminar a {name} del canal?',
    'channel.members.transferOwnership': 'Transferir propiedad',
    'channel.members.transferConfirm': '¿Transferir la propiedad a {name}?\n\nTe convertirás en administrador después de la transferencia.',
    'channel.members.ownerCount': 'Propietario {count}',
    'channel.members.adminCount': 'Administrador {count}',
    'channel.members.memberCount': 'Miembro {count}',
    'channel.members.noMembers': 'Sin miembros',
    'channel.members.totalCount': '{count} miembros',

    // Channel Settings
    'channel.settings.title': 'Configuración del canal',
    'channel.settings.name': 'Nombre del canal',
    'channel.settings.namePlaceholder': 'Introduce el nombre del canal',
    'channel.settings.description': 'Descripción',
    'channel.settings.descriptionPlaceholder': 'Una breve descripción del canal',
    'channel.settings.icon': 'Icono del canal',
    'channel.settings.iconChange': 'Clic para cambiar',
    'channel.settings.save': 'Guardar',
    'channel.settings.leave': 'Abandonar canal',
    'channel.settings.leaveWarning': 'Una vez que abandones, no podrás volver a unirte a menos que te inviten de nuevo.',
    'channel.settings.leaveOwnerWarning': 'Como propietario, solo puedes abandonar después de transferir la propiedad.',
    'channel.settings.errorNameEmpty': 'Por favor introduce el nombre del canal',
    'channel.settings.errorNameShort': 'El nombre del canal debe tener al menos 2 caracteres',
    'channel.settings.errorNameLong': 'El nombre del canal puede tener máximo 50 caracteres',
    'channel.settings.errorFileSize': 'El archivo debe ser de 5MB o menos',
    'channel.settings.errorUpload': 'Error al subir el icono',
    'channel.settings.errorSave': 'Error al guardar la configuración del canal',
    'channel.settings.errorLeave': 'Error al abandonar el canal',
    'channel.settings.errorOwnerLeave': 'El propietario debe transferir la propiedad o eliminar el canal',
    'channel.settings.errorNeedAdmin': 'El canal necesita al menos 1 administrador',

    // Channel Invite
    // Channel Chat
    'channel.chat.messagePlaceholder': 'Mensaje a #{name}',

    'channel.invite.title': 'Invitar al canal',
    'channel.invite.expiration': 'Expira en',
    'channel.invite.1h': '1 hora',
    'channel.invite.1d': '1 día',
    'channel.invite.7d': '7 días',
    'channel.invite.never': 'Nunca',
    'channel.invite.generate': 'Generar enlace de invitación',
    'channel.invite.copied': '¡Copiado al portapapeles!',
    'channel.invite.generated': 'Enlace generado',
    'channel.invite.copyAgain': 'Copiar de nuevo',
    'channel.invite.noExpiry': 'Sin fecha de expiración',
    'channel.invite.errorGenerate': 'Error al generar el enlace de invitación',
    'channel.invite.loginRequired': 'Inicio de sesión requerido',
    'channel.invite.loginRequiredDesc': 'Inicia sesión para unirte a un canal.',
    'channel.invite.login': 'Iniciar sesión',
    'channel.invite.noCode': 'Sin código de invitación',
    'channel.invite.noCodeDesc': 'Accede mediante un enlace de invitación válido.',
    'channel.invite.goToChannels': 'Ir a canales',

    // Channel Errors
    'channel.error.notFound': 'Canal no encontrado',
    'channel.error.noAccess': 'No tienes acceso a este canal',
    'channel.error.loadFailed': 'Error al cargar el canal',
    'channel.error.loginRequired': 'Inicio de sesión requerido',
    'channel.error.roleChangeFailed': 'Error al cambiar el rol',
    'channel.error.cannotManageOwner': 'No se puede cambiar el rol del propietario',
    'channel.error.cannotManageAdmin': 'Los administradores no pueden gestionar a otros administradores',
    'channel.error.transferFailed': 'Error al transferir la propiedad',
    'channel.error.kickFailed': 'Error al expulsar al miembro',
    'channel.error.kicked': 'Has sido expulsado de este canal',
    'channel.error.needJoin': 'Debes unirte al canal para enviar mensajes',
    'channel.error.ownerCannotLeave': 'El propietario debe transferir la propiedad o eliminar el canal',

    // Channel Join
    'channel.join.join': 'Unirse',
    'channel.join.cancel': 'Cancelar',
    'channel.join.goBack': 'Volver',
    'channel.join.memberCount': '{count} miembros',
    'channel.join.invalidLink': 'Enlace de invitación inválido',
    'channel.join.invalidDesc': 'Este enlace de invitación no existe o ha sido eliminado.',
    'channel.join.usedLink': 'Enlace de invitación ya usado',
    'channel.join.usedDesc': 'Este enlace de invitación ya ha sido usado.',
    'channel.join.expiredLink': 'Enlace de invitación expirado',
    'channel.join.expiredDesc': 'Este enlace de invitación ha expirado.',
    'channel.join.selfLink': 'Tu propio enlace de invitación',
    'channel.join.selfDesc': 'Este es tu propio enlace de invitación. Redirigiendo a la sala de chat.',
    'channel.join.maxUsed': 'Máximo de usos alcanzado',
    'channel.join.maxUsedDesc': 'Este enlace de invitación ha alcanzado su máximo de usos. Por favor solicita un nuevo enlace.',
    'channel.join.alreadyMember': 'Ya eres miembro',
    'channel.join.alreadyMemberDesc': 'Ya eres miembro de este canal.',
    'channel.join.channelFull': 'Canal lleno',
    'channel.join.channelFullDesc': 'Este canal ha alcanzado su capacidad máxima.',
    'channel.join.notFoundLink': 'Enlace de invitación no encontrado',
    'channel.join.notFoundDesc': 'Enlace de invitación inválido. Por favor verifica el enlace.',

    // DM / Messages
    'dm.error.notFound': 'Conversación no encontrada',
    'dm.error.loadFailed': 'Error al cargar la conversación',
    'dm.blocked.byMe': 'Has bloqueado a este usuario. Desbloquea para enviar mensajes.',
    'dm.blocked.byThem': 'No puedes enviar mensajes.',
    'dm.blocked.mutual': 'Usuario bloqueado. No puedes enviar ni recibir mensajes.',
    'dm.placeholder': 'Mensaje a @{name}',
    'dm.placeholderDefault': 'Escribe un mensaje...',
    'dm.placeholderBlocked': 'No puedes enviar mensajes',
    'dm.offline': 'Estás sin conexión. Los mensajes se enviarán cuando se restablezca la conexión.',
    'dm.user': 'Usuario',

    // Time
    'time.justNow': 'Ahora mismo',
    'time.minuteAgo': 'hace 1 min',
    'time.minutesAgo': 'hace {count} min',
    'time.hourAgo': 'hace 1 h',
    'time.hoursAgo': 'hace {count} h',
    'time.yesterday': 'Ayer',
    'time.dayAgo': 'hace 1 día',
    'time.daysAgo': 'hace {count} días',
    'time.requested': 'Solicitado ',
    'time.sent': 'Enviado ',

    // Language names (for profile display)
    'language.ko': 'Coreano',
    'language.en': 'Inglés',
    'language.ja': 'Japonés',
    'language.zh': 'Chino',
    'language.es': 'Español',
    'language.fr': 'Francés',
    'language.de': 'Alemán',
    'language.pt': 'Portugués',
    'language.ru': 'Ruso',
    'language.ar': 'Árabe',
    'language.hi': 'Hindi',
    'language.vi': 'Vietnamita',
    'language.th': 'Tailandés',
    'language.id': 'Indonesio',

    // System messages
    'system.memberJoined': '{name} se unió al canal',
    'system.memberLeft': '{name} salió del canal',
    'system.memberKicked': '{name} fue expulsado',
    'system.channelUpdated': 'La información del canal se actualizó',
    'system.roleChanged': 'El rol de {name} cambió a {role}',
    'system.ownershipTransferred': 'La propiedad del canal se transfirió a {name}',

    // Friend Requests
    'friendRequest.accept': 'Aceptar',
    'friendRequest.decline': 'Rechazar',
    'friendRequest.cancel': 'Cancelar solicitud',
    'friendRequest.processing': 'Procesando...',
    'friendRequest.acceptFailed': 'Error al aceptar',
    'friendRequest.declineFailed': 'Error al rechazar',
    'friendRequest.cancelFailed': 'Error al cancelar',
    'friendRequest.noReceived': 'No hay solicitudes recibidas',
    'friendRequest.noSent': 'No hay solicitudes enviadas',
    'friendRequest.noReceivedDesc': 'Las solicitudes de amistad de otros aparecerán aquí',
    'friendRequest.noSentDesc': 'Tus solicitudes de amistad enviadas aparecerán aquí',

    // Search
    'search.placeholder': 'Buscar por etiqueta o nombre de usuario',
    'search.clear': 'Borrar búsqueda',
    'search.noResults': 'No se encontró usuario para "{query}"',
    'search.tagHelp': 'Introduce la etiqueta completa (ej: john#A1B2) para una búsqueda exacta',

    // Misc
    'misc.startConversation': 'Iniciar conversación',
    'misc.waitingForPartner': 'Esperando al compañero...',
    'misc.chat': 'Chat',
    'misc.newConversation': 'Nueva conversación',
    'misc.waiting': 'Esperando...',
    'misc.copyLink': 'Copiar enlace',
    'misc.linkCopied': '¡Enlace copiado!',
    'misc.unknownError': 'Ocurrió un error desconocido',
    'misc.withdrawnUser': 'Usuario eliminado',
    'misc.selectConversation': 'Selecciona una conversación',
    'misc.selectConversationDesc': 'Selecciona una conversación de la lista o inicia una nueva.',
    'misc.selectChannel': 'Selecciona un canal',
    'misc.selectChannelDesc': 'Selecciona un canal de la lista para ver las conversaciones.',
  },
} as const;

/**
 * Get translation for a key
 */
export function getTranslation(key: TranslationKey, language: LanguageCode = 'en'): string {
  const langTranslations = translations[language] || translations.en;
  return langTranslations[key] || translations.en[key] || key;
}

/**
 * Get translation with placeholder replacement
 * e.g., t('addFriend.successMessage', { name: 'John' }) => "Friend request sent to John"
 */
export function getTranslationWithParams(
  key: TranslationKey,
  params: Record<string, string>,
  language: LanguageCode = 'en'
): string {
  let text = getTranslation(key, language);

  Object.entries(params).forEach(([paramKey, paramValue]) => {
    text = text.replace(`{${paramKey}}`, paramValue);
  });

  return text;
}

export default translations;
